import React from "react";
import { Link } from "react-router-dom";
import { Instagram, Facebook, Phone, Mail, MapPin, Sparkles } from "lucide-react";
import { useSiteContent } from "@/lib/siteContent";

export default function Footer() {
  const { content } = useSiteContent();
  const site = content?.site || {};
  const has = (v) => v && String(v).trim().length > 0;
  const hasContact = has(site.contactPhone) || has(site.contactEmail) || has(site.serviceArea);
  const hasSocial = has(site.instagramHandle) || has(site.facebookHandle);

  return (
    <footer className="bg-cozy-brown text-cozy-bg pt-20 pb-10" data-testid="site-footer">
      <div className="max-w-7xl mx-auto px-5 lg:px-10 grid md:grid-cols-4 gap-10">
        <div>
          <div className="flex items-center gap-3 mb-4">
            <img
              src="https://customer-assets.emergentagent.com/job_teepee-magic-nights/artifacts/g1w0mtsx_the%20cozy%20cove%20logo.jpeg"
              alt="The Cozy Cove"
              className="w-12 h-12 rounded-full object-cover border border-cozy-gold/30"
            />
            <div>
              <div className="font-serif text-xl">The Cozy Cove</div>
              <div className="font-script text-cozy-gold text-sm -mt-1">magical sleepovers</div>
            </div>
          </div>
          <p className="text-sm opacity-80 leading-relaxed">
            Luxury indoor teepee sleepover experiences delivered with love.
          </p>
          <Link
            to="/booking"
            data-testid="footer-cta-book"
            className="inline-flex mt-5 px-5 py-2.5 rounded-full bg-cozy-gold text-white text-sm font-medium hover:bg-cozy-gold-dark transition shadow-cozy"
          >
            Book Your Party
          </Link>
        </div>

        <div>
          <h4 className="font-serif text-lg mb-4 text-cozy-gold">Quick Links</h4>
          <ul className="space-y-2 text-sm">
            <li><Link to="/booking" data-testid="footer-link-packages" className="opacity-80 hover:opacity-100 hover:text-cozy-gold transition">Packages</Link></li>
            <li><Link to="/themes" data-testid="footer-link-themes" className="opacity-80 hover:opacity-100 hover:text-cozy-gold transition">Themes</Link></li>
            <li><Link to="/" data-testid="footer-link-gallery" className="opacity-80 hover:opacity-100 hover:text-cozy-gold transition">Gallery</Link></li>
            <li><Link to="/contact" data-testid="footer-link-contact" className="opacity-80 hover:opacity-100 hover:text-cozy-gold transition">Contact</Link></li>
            <li><Link to="/faq" data-testid="footer-link-faq" className="opacity-80 hover:opacity-100 hover:text-cozy-gold transition">FAQ</Link></li>
          </ul>
        </div>

        {hasContact && (
          <div>
            <h4 className="font-serif text-lg mb-4 text-cozy-gold">Get In Touch</h4>
            <ul className="space-y-3 text-sm">
              {has(site.contactPhone) && (
                <li className="flex items-center gap-2 opacity-90" data-testid="footer-phone">
                  <Phone className="w-4 h-4 text-cozy-gold flex-shrink-0" />{site.contactPhone}
                </li>
              )}
              {has(site.contactEmail) && (
                <li className="flex items-center gap-2 opacity-90" data-testid="footer-email">
                  <Mail className="w-4 h-4 text-cozy-gold flex-shrink-0" />{site.contactEmail}
                </li>
              )}
              {has(site.serviceArea) && (
                <li className="flex items-start gap-2 opacity-90" data-testid="footer-area">
                  <MapPin className="w-4 h-4 text-cozy-gold flex-shrink-0 mt-0.5" />
                  <span>
                    {site.serviceArea}{" "}
                    <Link to="/contact" className="text-cozy-gold hover:underline whitespace-nowrap">
                      (see service map)
                    </Link>
                  </span>
                </li>
              )}
            </ul>
          </div>
        )}

        {hasSocial && (
          <div>
            <h4 className="font-serif text-lg mb-4 text-cozy-gold">Follow Along</h4>
            <div className="flex gap-3">
              {has(site.instagramHandle) && (
                <a href="https://instagram.com" target="_blank" rel="noreferrer" data-testid="footer-instagram" className="w-10 h-10 rounded-full bg-white/10 hover:bg-cozy-gold flex items-center justify-center transition">
                  <Instagram className="w-5 h-5" />
                </a>
              )}
              {has(site.facebookHandle) && (
                <a href="https://facebook.com" target="_blank" rel="noreferrer" data-testid="footer-facebook" className="w-10 h-10 rounded-full bg-white/10 hover:bg-cozy-gold flex items-center justify-center transition">
                  <Facebook className="w-5 h-5" />
                </a>
              )}
            </div>
            <p className="text-xs opacity-70 mt-5">
              {[site.instagramHandle, site.facebookHandle].filter(has).join(" · ")}
            </p>
          </div>
        )}
      </div>

      <div className="border-t border-white/10 mt-12 pt-6 text-center text-xs opacity-70">
        <p className="font-script text-base text-cozy-pink">Family-Owned · Made With Love</p>
        <p className="mt-2">© {new Date().getFullYear()} The Cozy Cove. All rights reserved.</p>
        <p className="mt-2 text-[10px] opacity-60">
          <a
            href="https://www.willowmanagement.net/"
            target="_blank"
            rel="noopener noreferrer"
            className="hover:text-cozy-gold transition underline-offset-2 hover:underline"
            data-testid="footer-credit"
          >
            Web Design
          </a>{" "}
          by Willow Management
        </p>
      </div>
    </footer>
  );
}
